import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import { IconButton } from '../../../../CommonComponents/OPL/IconButton/IconButton';
import { Input } from '../../../../CommonComponents/OPL/Input/Input';
import { InputMask } from '../../../../CommonComponents/OPL/Input/InputMask';
import { Select } from '../../../../CommonComponents/OPL/Select/Select/Select';
import { Tooltip } from '../../../../CommonComponents/OPL/Tooltip/Tooltip';
import { FormValues } from '../../PersonalInfo.meta';
import { SectionHeading } from '../../PersonalInfo.styles';
import { FilingStatus, PersonalInfoDTO } from '../../api/usePersonalInfoQuery';
import { PartnerInfoGrid } from './PartnerInfo.styles';
import { Separator } from '../../../../CommonComponents/OPL/Separator/Separator';

interface Props {
  showPartnerSection: boolean;
  onAddPartner(): void;
  onDeletePartner(): void;
  data: PersonalInfoDTO;
  isEstatePlanning: boolean;
}

export const PartnerInfo: React.FC<Props> = ({
  showPartnerSection,
  onAddPartner,
  onDeletePartner,
  data,
  isEstatePlanning,
}) => {
  const {
    control,
    register,
    watch,
    formState: { errors, disabled: isReadOnly },
  } = useFormContext<FormValues>();

  const filingStatus = watch('personalInfo.filingStatus');

  if (!showPartnerSection && isReadOnly) {
    return null;
  }

  return (
    <>
      <section>
        <SectionHeading as="h2">Partner Info</SectionHeading>

        {showPartnerSection && (
          <PartnerInfoGrid>
            <Input
              {...register('personalInfo.partner.firstName')}
              label="First Name"
              placeholder="First"
              formVariant={errors.personalInfo?.partner?.firstName ? 'error' : undefined}
              helperText={errors.personalInfo?.partner?.firstName?.message}
              autoComplete="given-name"
            />

            <Input
              {...register('personalInfo.partner.lastName')}
              label="Last Name"
              placeholder="Last"
              autoComplete="family-name"
            />

            {!isReadOnly ? (
              <Tooltip
                content="To delete the partner please change your filing status."
                disabled={
                  filingStatus !== FilingStatus.MarriedFilingJointly &&
                  filingStatus !== FilingStatus.MarriedFilingSeparate
                }
              >
                <span>
                  <IconButton
                    icon="far fa-minus-circle"
                    variant="subtleInactive"
                    mt="24"
                    onClick={onDeletePartner}
                    aria-label="Delete Partner"
                    disabled={
                      filingStatus === FilingStatus.MarriedFilingJointly ||
                      filingStatus === FilingStatus.MarriedFilingSeparate
                    }
                  />
                </span>
              </Tooltip>
            ) : (
              <span />
            )}

            <InputMask
              {...register('personalInfo.partner.dob')}
              label="Date of Birth"
              mask="99/99/9999"
              placeholder="MM/DD/YYYY"
              leftIcon="far fa-calendar-alt"
              formVariant={errors.personalInfo?.partner?.dob ? 'error' : undefined}
              helperText={errors.personalInfo?.partner?.dob?.message}
              autoComplete="bday"
            />

            {isEstatePlanning && (
              <Controller
                control={control}
                name="personalInfo.spouseTaxFilingState"
                render={({ field }) => (
                  <Select
                    label="Filing State"
                    value={data.FilingStateOptions.find(({ value }) => value === field.value)}
                    onChange={opt => {
                      if (opt) {
                        field.onChange(opt.value);
                      }
                    }}
                    options={data.FilingStateOptions.filter(({ IsInsideUS }) => IsInsideUS)}
                    disabled={field.disabled}
                  />
                )}
              />
            )}
          </PartnerInfoGrid>
        )}

        {!showPartnerSection && (
          <Button leftIcon="fas fa-plus" variant="link" onClick={onAddPartner}>
            Add a partner
          </Button>
        )}
      </section>

      <Separator my="32" />
    </>
  );
};
